<template>
  <div>
    <Nav v-if="!embedded"></Nav>
    <b-container fluid>
      <b-row v-if="!embedded">
        <b-col>
          <h2>Artículos</h2>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col md="6" v-if="!embedded">
          <b-form-group label="Seleccionar Menú:">
            <b-form-select v-model="selectedMenuId" :options="menuOptions" @change="menuChanged"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col :md="embedded ? 12 : 6" class="d-flex align-items-end">
          <b-button
            size="sm"
            variant="success"
            @click="createItem"
            :disabled="!selectedMenuId">
            Crear Artículo
          </b-button>
        </b-col>
      </b-row>
      
      <b-row v-if="selectedMenuId">
        <b-col md="6">
          <b-form-group label="Filtrar por categoría:">
            <b-form-select v-model="selectedCategoryFilter" :options="categoryOptions">
              <template #first>
                <option value="">Todas las categorías</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Buscar:">
            <b-form-input type="search" v-model="search" debounce="500"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      
      <b-alert :show="!selectedMenuId" variant="info">
        Selecciona un menú para ver sus artículos
      </b-alert>
      
      <b-row v-if="selectedMenuId">
        <b-col>
          <b-table 
            small 
            responsive 
            striped 
            hover 
            :items="filteredItems" 
            :fields="camposTabla"
            :filter="search"
            :busy="isLoading">
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Cargando...</strong>
              </div>
            </template>
            <template #cell(orden)="data">
              <b-button-group size="sm">
                <b-button variant="outline-secondary"
                  :disabled="reordenando || indiceEn(data.item) <= 0"
                  title="Subir" @click="moverItem(data.item, -1)">▲</b-button>
                <b-button variant="outline-secondary"
                  :disabled="reordenando || indiceEn(data.item) >= filteredItems.length - 1"
                  title="Bajar" @click="moverItem(data.item, 1)">▼</b-button>
              </b-button-group>
            </template>
            <template #cell(image_url)="data">
              <b-img v-if="data.item.image_url" thumbnail :src="imageSrc(data.item.image_url)" width="50" height="50" />
              <span v-else class="text-muted">Sin imagen</span>
            </template>
            <template #cell(price)="data">
              ${{ formatPrice(data.item.price) }}
            </template>
            <template #cell(type)="data">
              <b-badge :variant="getTypeVariant(data.item.type)">
                {{ data.item.type }}
              </b-badge>
            </template>
            <template #cell(is_active)="data">
              <b-badge variant="success" v-if="data.item.is_active">Activo</b-badge>
              <b-badge variant="danger" v-else>Inactivo</b-badge>
            </template>
            <template #cell(categories)="data">
              <b-badge 
                v-for="cat in getItemCategories(data.item)" 
                :key="cat.id" 
                variant="info" 
                class="mr-1 mb-1">
                {{ cat.name }}
              </b-badge>
              <b-button 
                v-if="data.item.id" 
                size="sm" 
                variant="outline-primary" 
                @click="manageCategories(data.item)"
                class="mt-1">
                <i class="fas fa-edit"></i>
              </b-button>
            </template>
            <template #cell(buttons)="data">
              <b-button-group>
                <b-button size="sm" variant="warning" @click="editItem(data.item)">
                  Editar
                </b-button>
                <b-button size="sm" variant="info" @click="manageModifiers(data.item)">
                  Modificadores
                </b-button>
                <b-button size="sm" variant="danger" @click="confirmDeleteItem(data.item.id)">
                  Eliminar
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-container>

    <!-- Create/Edit Item Modal -->
    <b-modal 
      id="modal-item" 
      :title="isEditing ? 'Editar Artículo' : 'Crear Artículo'" 
      size="lg"
      @ok.prevent="submitItem"
      @hidden="resetItemForm">
      <form ref="itemForm" @submit.stop.prevent="submitItem">
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nombre:"
              label-for="name-input"
              :state="nameValidation"
              :invalid-feedback="'El nombre es requerido'">
              <b-form-input
                id="name-input"
                v-model="currentItem.name"
                :state="nameValidation"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="SKU:"
              label-for="sku-input">
              <b-form-input
                id="sku-input"
                v-model="currentItem.sku"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-form-group label="Descripción:">
          <b-form-textarea
            v-model="currentItem.description"
            placeholder="Descripción del artículo"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
        
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo:">
              <b-form-select v-model="currentItem.type" :options="itemTypeOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Precio:"
              label-for="price-input"
              :state="priceValidation"
              :invalid-feedback="'El precio es requerido'">
              <b-form-input
                id="price-input"
                v-model.number="currentItem.price"
                type="number"
                min="0"
                step="0.01"
                :state="priceValidation"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Precio Promocion:"
              label-for="price-input">
              <b-form-input
                id="price-input"
                v-model.number="currentItem.promo_price"
                type="number"
                min="0"
                step="0.01"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Texto Promocion:"
              label-for="price-input">
              <b-form-input
                id="price-input"
                v-model.number="currentItem.promo_text"
                type="text"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-row>
          <b-col md="6">
            <b-form-group label="Producto o personalización:">
              <vu-select v-model="currentRef" label="text"
                :reduce="(x) => x.value"
                :options="productOptions"></vu-select>
              <small class="text-muted">
                Una personalización se adjunta a la línea del ticket; un producto genera su propia línea.
              </small>
              </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Límite máximo:">
              <b-form-input
                v-model.number="currentItem.max_limit"
                type="number"
                min="0"
              ></b-form-input>
              <small class="form-text text-muted">0 = Sin límite</small>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-row>
          <b-col md="6">
            <b-form-group label="Disponible desde:">
              <b-form-input
                v-model="currentItem.available_from"
                type="datetime-local"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Disponible hasta:">
              <b-form-input
                v-model="currentItem.available_to"
                type="datetime-local"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-row>
          <b-col md="6">
            <b-form-group label="Pre-venta desde:" description="Visible antes de esta fecha solo para el nivel mínimo.">
              <b-form-input v-model="currentItem.presale_from" type="datetime-local"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Nivel mínimo de pre-venta (código):">
              <b-form-input v-model="currentItem.presale_min_tier_code" placeholder="oro"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Imagen:">
          <b-form-file
            accept="image/*"
            :disabled="uploadingImage"
            placeholder="Sube una imagen o pega una URL abajo"
            browse-text="Subir"
            @input="onImageSelected"
          ></b-form-file>
          <b-form-input
            class="mt-2"
            v-model="currentItem.image_url"
            placeholder="https://example.com/image.jpg"
          ></b-form-input>
          <div v-if="uploadingImage" class="text-muted mt-2">
            <b-spinner small></b-spinner> Subiendo imagen...
          </div>
          <b-img
            v-else-if="currentItem.image_url"
            thumbnail
            :src="imageSrc(currentItem.image_url)"
            width="80"
            height="80"
            class="mt-2"
          />
        </b-form-group>
        
        <b-form-group>
          <b-form-checkbox v-model="currentItem.is_active">
            Artículo Activo
          </b-form-checkbox>
        </b-form-group>
      </form>
    </b-modal>

    <!-- Manage Categories Modal -->
    <b-modal 
      id="modal-manage-categories" 
      title="Administrar Categorías" 
      @ok="saveCategoryAssignments"
      @hidden="resetCategoryModal">
      <p class="mb-3">Selecciona las categorías para: <strong>{{ currentItem.name }}</strong></p>
      
      <b-form-group>
        <b-form-checkbox-group 
          v-model="selectedCategories" 
          :options="categoryAssignmentOptions"
          stacked
        ></b-form-checkbox-group>
      </b-form-group>
    </b-modal>

    <!-- Manage Modifiers Modal -->
    <b-modal 
      id="modal-manage-modifiers" 
      title="Administrar Grupos de Modificadores" 
      size="lg"
      @ok="saveModifierAssignments"
      @hidden="resetModifierModal">
      <p class="mb-3">Selecciona los grupos de modificadores para: <strong>{{ currentItem.name }}</strong></p>
      
      <b-table 
        small
        responsive
        striped
        hover
        :items="modifierGroups"
        :fields="modifierGroupFields">
        <template #cell(actions)="data">
          <b-form-checkbox 
            v-model="selectedModifierGroups" 
            :value="data.item.id"
            switch
          ></b-form-checkbox>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import svc from "../../services/platformMenuService";
import Nav from "../Nav.vue";
export default {
  name: "PlatformItems",
  components: { Nav },
  props: {
    embedded: { type: Boolean, default: false },
    menuId: { type: [String, Number], default: "" }
  },
  data() {
    return {
      items: [],
      menus: [],
      categories: [],
      products: [],
      personalizations: [],
      categoryAssignments: [],
      reordenando: false,
      modifierGroups: [],
      selectedMenuId: "",
      selectedCategoryFilter: "",
      selectedCategories: [],
      selectedModifierGroups: [],
      isLoading: false,
      isLoadingCategories: false,
      isLoadingModifiers: false,
      isEditing: false,
      uploadingImage: false,
      search: "",
      currentItem: {
        id: "",
        menu_id: "",
        external_id: 0,
        sku: "",
        name: "",
        description: "",
        type: "PRODUCT",
        promo_price:null,
        promo_text:null,
        presale_from: null,
        presale_min_tier_code: null,
        price: 0,
        image_url: "",
        available_from: this.formatDateTimeForInput(new Date()),
        available_to: this.formatDateTimeForInput(this.getNextYear()),
        is_active: true,
        max_limit: 0
      },
      tableFields: [
        { key: "image_url", label: "Imagen" },
        { key: "name", label: "Nombre", sortable: true },
        { key: "sku", label: "SKU", sortable: true },
        { key: "type", label: "Tipo", sortable: true },
        { key: "price", label: "Precio", sortable: true },
        { key: "categories", label: "Categorías" },
        { key: "external_id", label: "ID", sortable: true },
        { key: "is_active", label: "Estado", sortable: true },
        { key: "buttons", label: "Acciones" }
      ],
      modifierGroupFields: [
        { key: "name", label: "Nombre", sortable: true },
        { key: "required_min", label: "Mín", sortable: true },
        { key: "required_max", label: "Máx", sortable: true },
        { key: "is_active", label: "Estado", sortable: true },
        { key: "actions", label: "Seleccionar" }
      ],
      itemTypeOptions: [
        { value: "PRODUCT", text: "Producto" },
        { value: "MODIFIER", text: "Modificador" }
      ]
    };
  },
  computed: {
    menuOptions() {
      const options = this.menus.map(menu => ({
        value: menu.id,
        text: menu.name
      }));
      
      return [
        { value: "", text: "Selecciona un menú", disabled: true },
        ...options
      ];
    },
    // Un solo selector para las dos cosas a las que puede apuntar external_id.
    // El valor lleva el tipo dentro ("product::1920") porque el id solo es
    // ambiguo: el 67 puede ser el producto 67 o la personalización 67.
    productOptions() {
      return [
        ...this.products.map(p => ({
          value: `product::${p.id}`,
          text: `Producto · ${p.id} - ${p.clave} - ${p.ndescription}`
        })),
        ...this.personalizations.map(p => ({
          value: `personalization::${p.id}`,
          text: `Personalización · ${p.id} - ${p.name || p.short_name}`
        }))
      ];
    },
    // Empaqueta/desempaqueta external_id + ref_type para el selector.
    currentRef: {
      get() {
        if (!this.currentItem.external_id) return null;
        const t = this.currentItem.ref_type === "personalization" ? "personalization" : "product";
        return `${t}::${this.currentItem.external_id}`;
      },
      set(val) {
        if (!val) {
          this.currentItem.external_id = "";
          this.currentItem.ref_type = "product";
          return;
        }
        const [tipo, id] = String(val).split("::");
        this.currentItem.ref_type = tipo === "personalization" ? "personalization" : "product";
        this.currentItem.external_id = id;
      }
    },
    categoryOptions() {
      return this.categories.map(category => ({
        value: category.id,
        text: category.name
      }));
    },
    categoryAssignmentOptions() {
      return this.categories.map(category => ({
        value: category.id,
        text: category.name
      }));
    },
    nameValidation() {
      return this.currentItem.name ? true : false;
    },
    priceValidation() {
      return this.currentItem.price >= 0 ? true : false;
    },
    filteredItems() {
      if (!this.selectedCategoryFilter) {
        return this.items;
      }
      const cat = this.selectedCategoryFilter;
      // Al filtrar por categoría se respeta el orden guardado, que es el que ve
      // el cliente en la app. Sin categoría no hay orden que mostrar: el mismo
      // producto puede ir en distinta posición en cada categoría.
      return this.items
        .filter(item => this.getItemCategories(item).some(c => c.id === cat))
        .slice()
        .sort((a, b) => this.posicionEn(a, cat) - this.posicionEn(b, cat));
    },
    // El reordenamiento solo tiene sentido dentro de una categoría.
    puedeReordenar() {
      return !!this.selectedCategoryFilter;
    },
    camposTabla() {
      if (!this.puedeReordenar) return this.tableFields;
      return [{ key: "orden", label: "Orden" }, ...this.tableFields];
    }
  },
  mounted() {
    this.getMenus();
    this.getProducts();
    this.getPersonalizations();

    if (this.embedded) {
      if (this.menuId) {
        this.selectedMenuId = this.menuId;
        this.menuChanged();
      }
    } else if (this.$route.params.menu_id) {
      // Check if menu_id is in route params
      this.selectedMenuId = this.$route.params.menu_id;
      this.menuChanged();

      // If category_id is also present, set the filter
      if (this.$route.params.category_id) {
        this.selectedCategoryFilter = this.$route.params.category_id;
      }
    }
  },
  watch: {
    menuId(val) {
      if (!this.embedded) return;
      this.selectedMenuId = val || "";
      if (this.selectedMenuId) {
        this.menuChanged();
      } else {
        this.items = [];
        this.categories = [];
        this.modifierGroups = [];
      }
    }
  },
  methods: {
    getMenus() {
      axios
        .get("/platform/menus")
        .then(response => {
          this.menus = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener menús", "danger");
        });
    },
    getPersonalizations() {
      axios
        .get("/personalizations")
        .then(response => {
          this.personalizations = Array.isArray(response.data) ? response.data : [];
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener personalizaciones", "danger");
        });
    },
    getProducts() {
      axios
        .get("/products")
        .then(response => {
          this.products = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener productos", "danger");
        });
    },
    menuChanged() {
      this.selectedCategoryFilter = "";
      this.getItems();
      this.getCategories();
      this.getModifierGroups();
    },
    getItems() {
      if (!this.selectedMenuId) return;
      
      this.isLoading = true;
      axios
        .get(`/platform/menu/${this.selectedMenuId}/items`)
        .then(response => {
          this.items = response.data;
          this.getCategoryAssignments();
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener artículos", "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getCategories() {
      if (!this.selectedMenuId) return;
      
      this.isLoadingCategories = true;
      axios
        .get(`/platform/menu/${this.selectedMenuId}/categories`)
        .then(response => {
          this.categories = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener categorías", "danger");
        })
        .finally(() => {
          this.isLoadingCategories = false;
        });
    },
    getModifierGroups() {
      if (!this.selectedMenuId) return;
      
      this.isLoadingModifiers = true;
      axios
        .get(`/platform/menu/${this.selectedMenuId}/modifiergroups`)
        .then(response => {
          this.modifierGroups = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener grupos de modificadores", "danger");
        })
        .finally(() => {
          this.isLoadingModifiers = false;
        });
    },
    // Una sola llamada: antes se pedía /platform/item/{id}/categories por cada
    // item —195 peticiones en un menú grande— y además se tiraba el
    // sorting_position, que es justo lo que define el orden de los productos.
    getCategoryAssignments() {
      if (!this.selectedMenuId) {
        this.categoryAssignments = [];
        return Promise.resolve();
      }
      return svc
        .unified(this.selectedMenuId)
        .then(response => {
          const ci = (response.data && response.data.category_items) || [];
          this.categoryAssignments = ci.map(a => ({
            item_id: a.item_id,
            category_id: a.category_id,
            sorting_position: Number(a.sorting_position) || 0,
          }));
        })
        .catch(err => {
          console.error("Error cargando las categorías de los items:", err);
          this.categoryAssignments = [];
        });
    },
    // Índice dentro del orden GUARDADO, no de la fila pintada: la tabla permite
    // ordenar por nombre y ahí data.index ya no corresponde al orden real.
    indiceEn(item) {
      return this.filteredItems.findIndex(x => x.id === item.id);
    },
    // Posición del item dentro de la categoría filtrada.
    posicionEn(item, categoryId) {
      const a = this.categoryAssignments.find(
        x => x.item_id === item.id && x.category_id === categoryId
      );
      return a ? a.sorting_position : 0;
    },
    // Intercambia un item con su vecino y persiste AMBAS posiciones: el endpoint
    // escribe una fila a la vez, así que mover uno solo dejaría dos items con la
    // misma posición y el orden volvería a ser arbitrario.
    moverItem(item, direccion) {
      const cat = this.selectedCategoryFilter;
      if (!cat || this.reordenando) return;
      const lista = this.filteredItems;
      const i = lista.findIndex(x => x.id === item.id);
      const j = i + direccion;
      if (i < 0 || j < 0 || j >= lista.length) return;

      const vecino = lista[j];
      // Si las posiciones vienen empatadas (0 por defecto), se usan los índices
      // para desempatar; si no, el intercambio no movería nada.
      let pa = this.posicionEn(item, cat);
      let pb = this.posicionEn(vecino, cat);
      if (pa === pb) {
        pa = i;
        pb = j;
      }

      this.reordenando = true;
      Promise.all([
        svc.setItemSorting(cat, item.id, pb),
        svc.setItemSorting(cat, vecino.id, pa),
      ])
        .then(() => {
          this.aplicarPosicion(item.id, cat, pb);
          this.aplicarPosicion(vecino.id, cat, pa);
        })
        .catch(err => {
          this.makeToast(
            "Error",
            (err.response && err.response.data) || "No se pudo cambiar el orden",
            "danger"
          );
          // Se recarga para no dejar la pantalla mostrando un orden que no se guardó.
          this.getCategoryAssignments();
        })
        .finally(() => {
          this.reordenando = false;
        });
    },
    aplicarPosicion(itemId, categoryId, pos) {
      const a = this.categoryAssignments.find(
        x => x.item_id === itemId && x.category_id === categoryId
      );
      if (a) a.sorting_position = pos;
    },
    getItemCategories(item) {
      if (!item.id) return [];
      
      const categoryIds = this.categoryAssignments
        .filter(assignment => assignment.item_id === item.id)
        .map(assignment => assignment.category_id);
      
      return this.categories.filter(category => categoryIds.includes(category.id));
    },
    createItem() {
      this.isEditing = false;
      this.currentItem = {
        id: "",
        menu_id: this.selectedMenuId,
        external_id: "",
        ref_type: "product",
        sku: "",
        name: "",
        description: "",
        type: "PRODUCT",
        price: 0,
        image_url: "",
        available_from: this.formatDateTimeForInput(new Date()),
        available_to: this.formatDateTimeForInput(this.getNextYear()),
        presale_from: null,
        presale_min_tier_code: null,
        is_active: true,
        max_limit: 0
      };
      this.$bvModal.show("modal-item");
    },
    editItem(item) {
      this.isEditing = true;
      this.currentItem = { 
        ...item,
        external_id: item.external_id ? item.external_id.toString() : "",
        ref_type: item.ref_type === "personalization" ? "personalization" : "product",
        available_from: this.formatDateTimeForInput(new Date(item.available_from)),
        available_to: this.formatDateTimeForInput(new Date(item.available_to))
      };
      this.$bvModal.show("modal-item");
    },
    submitItem() {
      if (!this.currentItem.name || this.currentItem.price < 0) {
        return; // Validation failed
      }

      // Prepare the data - convert external_id from string to number if needed
      const itemData = { ...this.currentItem };
      if (itemData.external_id) {
        itemData.external_id = parseInt(itemData.external_id);
      } else {
        itemData.external_id = 0;
      }

      const method = this.isEditing ? "PUT" : "POST";
      const url = this.isEditing 
        ? `/platform/item/${this.currentItem.id}` 
        : `/platform/menu/${this.selectedMenuId}/item`;
      
      axios({
        method,
        url,
        data: itemData
      })
        .then(() => {
          this.makeToast(
            "Éxito", 
            `Artículo ${this.isEditing ? 'actualizado' : 'creado'} exitosamente`, 
            "success"
          );
          this.getItems();
          this.$bvModal.hide("modal-item");
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || `Error al ${this.isEditing ? 'actualizar' : 'crear'} el artículo`, 
            "danger"
          );
        });
    },
    confirmDeleteItem(id) {
      this.$bvModal.msgBoxConfirm("¿Estás seguro que deseas eliminar este artículo? Esta acción no se puede deshacer.", {
        title: "Confirmar eliminación",
        okVariant: "danger",
        okTitle: "Eliminar",
        cancelTitle: "Cancelar",
        centered: true
      })
      .then(result => {
        if (result) {
          this.deleteItem(id);
        }
      });
    },
    deleteItem(id) {
      axios
        .delete(`/platform/item/${id}`)
        .then(() => {
          this.makeToast("Éxito", "Artículo eliminado exitosamente", "success");
          this.getItems();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al eliminar el artículo", 
            "danger"
          );
        });
    },
    // Builds a browser-reachable src: full URLs pass through; relative POS paths
    // (e.g. "static/images/upload-xxx.png" returned by the upload endpoint) are
    // resolved against the backend base URL.
    imageSrc(url) {
      if (!url) return "";
      if (/^https?:\/\//.test(url)) return url;
      const base = (axios.defaults.baseURL || "").replace(/\/$/, "");
      return base + "/" + url.replace(/^\//, "");
    },
    // Uploads the chosen file to the generic image endpoint and stores the
    // returned path as the item image (same endpoint used for products).
    onImageSelected(file) {
      if (!file) return;
      this.uploadingImage = true;
      const formData = new FormData();
      formData.append("image", file);
      axios
        .post("/image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(response => {
          this.currentItem.image_url = response.data.fileName;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al subir la imagen", "danger");
        })
        .finally(() => {
          this.uploadingImage = false;
        });
    },
    manageCategories(item) {
      this.currentItem = { ...item };
      this.selectedCategories = this.categoryAssignments
        .filter(assignment => assignment.item_id === item.id)
        .map(assignment => assignment.category_id);
      
      this.$bvModal.show("modal-manage-categories");
    },
    saveCategoryAssignments() {
      // First, remove all existing assignments for this item
      const existingAssignments = this.categoryAssignments
        .filter(assignment => assignment.item_id === this.currentItem.id)
        .map(assignment => assignment.category_id);
      
      // Categories to remove
      const categoriesToRemove = existingAssignments.filter(
        categoryId => !this.selectedCategories.includes(categoryId)
      );
      
      // Categories to add
      const categoriesToAdd = this.selectedCategories.filter(
        categoryId => !existingAssignments.includes(categoryId)
      );
      
      // Process removals
      const removalPromises = categoriesToRemove.map(categoryId => 
        axios.delete(`/platform/category/${categoryId}/item/${this.currentItem.id}`)
      );
      
      // Process additions
      const additionPromises = categoriesToAdd.map(categoryId => 
        axios.post(`/platform/category/${categoryId}/item/${this.currentItem.id}`, { sorting_position: 0 })
      );
      
      // Wait for all operations to complete
      Promise.all([...removalPromises, ...additionPromises])
        .then(() => {
          this.makeToast("Éxito", "Categorías actualizadas exitosamente", "success");
          this.getCategoryAssignments();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al actualizar categorías", 
            "danger"
          );
        });
    },
    manageModifiers(item) {
      this.currentItem = { ...item };
      
      // Get the assigned modifier groups for this item
      axios
        .get(`/platform/item/${item.id}/modifiergroups`)
        .then(response => {
          const modifierGroups = response.data || [];
          this.selectedModifierGroups = modifierGroups.map(group => group.id);
          this.$bvModal.show("modal-manage-modifiers");
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al obtener grupos de modificadores", 
            "danger"
          );
        });
    },
    saveModifierAssignments() {
      // Get current assignments
      axios
        .get(`/platform/item/${this.currentItem.id}/modifiergroups`)
        .then(response => {
          const currentAssignments = response.data || [];
          const currentIds = currentAssignments.map(group => group.id);
          
          // Determine what to add and remove
          const toAdd = this.selectedModifierGroups.filter(id => !currentIds.includes(id));
          const toRemove = currentIds.filter(id => !this.selectedModifierGroups.includes(id));
          
          // Process the changes
          const promises = [];
          
          // Add new assignments
          for (const groupId of toAdd) {
            promises.push(
              axios.post(`/platform/item/${this.currentItem.id}/modifiergroup/${groupId}`, {
                sorting_position: 0
              })
            );
          }
          
          // Remove old assignments
          for (const groupId of toRemove) {
            promises.push(
              axios.delete(`/platform/item/${this.currentItem.id}/modifiergroup/${groupId}`)
            );
          }
          
          // Execute all changes
          return Promise.all(promises);
        })
        .then(() => {
          this.makeToast("Éxito", "Grupos de modificadores actualizados exitosamente", "success");
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al actualizar grupos de modificadores", 
            "danger"
          );
        });
    },
    resetItemForm() {
      this.currentItem = {
        id: "",
        menu_id: this.selectedMenuId,
        external_id: "",
        ref_type: "product",
        sku: "",
        name: "",
        description: "",
        type: "PRODUCT",
        price: 0,
        image_url: "",
        available_from: this.formatDateTimeForInput(new Date()),
        available_to: this.formatDateTimeForInput(this.getNextYear()),
        is_active: true,
        max_limit: 0
      };
      this.isEditing = false;
    },
    resetCategoryModal() {
      this.selectedCategories = [];
    },
    resetModifierModal() {
      this.selectedModifierGroups = [];
    },
    getNextYear() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      return date;
    },
    formatDateTimeForInput(date) {
      if (!date) return '';
      // Los items creados sin fechas quedan en la base como la fecha cero de
      // MySQL ("0000-00-00 00:00:00"), que da Invalid Date. Antes toISOString
      // lanzaba RangeError y tumbaba editItem completo: el modal nunca llegaba
      // a abrirse y el botón Editar parecía muerto.
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 16);
    },
    formatPrice(price) {
      return Number(price).toFixed(2);
    },
    getTypeVariant(type) {
      switch (type) {
        case 'PRODUCT':
          return 'primary';
        case 'MODIFIER':
          return 'success';
        default:
          return 'secondary';
      }
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>